import { useCallback, useEffect, useRef, useState } from "react";
import { ISummaryItem, IUser } from "../../../types/api";
import MidPopup from "../../../hoc/MidPopup/MidPopup";
import { capitalizeWords, debounce } from "../../../shared/utility";
import { searchUsers } from "../../../api/users/users";
import { toast } from "react-toastify";
import "./ChooseUserPopup.scss";
import { INACTIVE_MEMBER_TYPE } from "../../../shared/memberType";

interface IChooseUserPopupProps {
  onClose: () => void;
  showPopup: boolean;
  onConfirm: (user: IUser) => void;
  summaryItem?: ISummaryItem;
  onCancel?: () => void;
}
function ChooseUserPopup(props: IChooseUserPopupProps) {
  const { onClose, showPopup, onConfirm, onCancel} = props;
  const [searchString, setSearchString] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [searchedUsers, setSearchedUsers] = useState<IUser[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClose = (shouldCancel:boolean) => {
    setSearchString("");
    setSearchedUsers([]);
    setSelectedUser(null);
    onClose();

    if(shouldCancel) {
      onCancel && onCancel();
    }
  };
  useEffect(() => {
    if (selectedUser) {
      onConfirm(selectedUser);
      handleClose(false);
    }
  }, [selectedUser]);

  const lastWarningTimeRef = useRef<number>(0);

  const showWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current >= 5000) {
      lastWarningTimeRef.current = now;
      toast.warning("Inga användare hittades");
    }
  }, []);

  const latestUserSearchRef = useRef<string>("");

  const fetchSearchUsers = async (search: string) => {
    latestUserSearchRef.current = search;
    try {
      const response = await searchUsers(search, "admin", true);
      if (latestUserSearchRef.current !== search) {
        return;
      }
      if (response.length === 0) {
        showWarning();
      }

      // Someone waiting for their start date cannot be booked for yet.
      setSearchedUsers(
        response.filter(
          (user: IUser) => user.member_type !== INACTIVE_MEMBER_TYPE
        )
      );
    } catch (error: any) {
      toast.error(error);
    }
  };
  useEffect(() => {
    if (showPopup && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopup]);

  const handleSearchUsers = () => {
    fetchSearchUsers(searchString);
  };

  useEffect(() => {
    if (searchString.length > 2) {
      handleSearchUsers();
    } else {
      setSearchedUsers([]);
    }
  }, [searchString]);

  const handleChoosePlayer = (player: IUser) => {
    setSelectedUser(player);
  };
  const warningInSlot = props.summaryItem?.warning !== "" && props.summaryItem?.warning;

  return (
    <MidPopup
      onClose={() => handleClose(true)}
      showPopup={showPopup}
      leftTopElement={<span className="text-m">Välj medlem</span>}
      onConfirm={() => {
        if (selectedUser) {
          onConfirm(selectedUser);
        }
      }}
    >
      <div className="choose-users-popup-wrapper">
        {warningInSlot && (
          <div className="warning-text text-m">
            <i className="fa-regular fa-triangle-exclamation warning-icon"></i>{" "}
            {props.summaryItem?.warning}
          </div>
        )}
        <div className="user-input-btn-wrapper">
          <div className="search-input-wrapper">
            <input
              className="users-search-input text-m"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder="Sök på namn & medlemsnummer"
              ref={inputRef}
            ></input>
            <i className="fa-regular fa-magnifying-glass search-icon"></i>
          </div>
        </div>
        {searchedUsers.length > 0 &&
          searchedUsers.map((player, index) => {
            if (index > 4) {
              return null;
            }
            const isFrozen = player.frozen;
            let frozenClass = "";
            if (isFrozen) {
              frozenClass = "frozen";
            }
            return (
              <div
                key={player.member_id}
                className="player-wrapper pointer text-m"
                onClick={() => handleChoosePlayer(player)}
              >
                <div className={`player-name ${frozenClass}`}>
                  <span>
                    {capitalizeWords(player.first_name)}{" "}
                    {capitalizeWords(player.last_name)} ({player.member_type}){" "}
                    {isFrozen && " - medlem fryst"}
                  </span>
                </div>
                <div className="player-id">{player.member_id}</div>
                <a className="choose-player">Välj medlem</a>
              </div>
            );
          })}
      </div>
    </MidPopup>
  );
}

export default ChooseUserPopup;
